import { ReactNode } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';

import '../../Roles/roles.css';

/** One edited field: what it is now and what it becomes. */
export interface FieldChange {
  label: string;
  from: ReactNode;
  to: ReactNode;
}

/** The "Field: old → new" rows shown before any edit to a user is sent. */
export const ChangeList = ({ changes }: { changes: FieldChange[] }) => (
  <dl className="ud-change-list">
    {changes.map((c) => (
      <div key={c.label} className="ud-change-row">
        <dt>{c.label}</dt>
        <dd>
          <span className="ud-change-from">{c.from}</span>
          <span className="ud-change-arrow">→</span>
          <span className="ud-change-to">{c.to}</span>
        </dd>
      </div>
    ))}
  </dl>
);

interface ConfirmChangeDialogProps {
  open: boolean;
  /** The question, naming the change, e.g. "Change status from Active to Blocked?". */
  message: string;
  /** Per-field old → new rows, for edits touching more than one field. */
  changes?: FieldChange[];
  /** Extra consequence line (e.g. "revokes access immediately"). */
  note?: string;
  confirmLabel?: string;
  danger?: boolean;
  isSubmitting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * "Are you sure?" step for edits on the User Details page. Plain dashboard
 * buttons: the shared MUI theme is built for the dark mobile app.
 */
export const ConfirmChangeDialog = ({
  open,
  message,
  changes,
  note,
  confirmLabel,
  danger = false,
  isSubmitting = false,
  onCancel,
  onConfirm,
}: ConfirmChangeDialogProps) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onCancel} fullWidth maxWidth="xs">
      <DialogTitle>{t('dashboard.userDetails.confirm.title')}</DialogTitle>
      <DialogContent>
        <p className="ud-confirm-message">{message}</p>
        {changes && changes.length > 0 && <ChangeList changes={changes} />}
        {note && <p className="ud-confirm-note">{note}</p>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <button type="button" className="secondary-action-btn" onClick={onCancel} disabled={isSubmitting}>
          {t('dashboard.userDetails.cancel')}
        </button>
        <button
          type="button"
          className={danger ? 'danger-action-btn' : 'primary-action-btn'}
          onClick={onConfirm}
          disabled={isSubmitting}
        >
          {confirmLabel ?? t('dashboard.userDetails.confirm.confirm')}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmChangeDialog;
